<template>
  <LandingCard v-if="items.length" title="Action Needed">
    <LandingCardEntityList title="Class Invitations" :items="items" variant="warn">
      <template #default="{ item }">
        <LandingCardEntityListItem :primary-label="item.primaryLabel" :secondary-label="item.secondaryLabel">
          <template #additionalInfo>
            <div
              class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
              v-if="item.additionalInfo"
            >
              {{ item.additionalInfo }}
            </div>
          </template>
        </LandingCardEntityListItem>
      </template>
    </LandingCardEntityList>
  </LandingCard>
</template>

<script lang="ts">
import type { LandingCardEntityBase } from "@/components/landing/landingCard";
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import { LE } from "@/constants/enums";
import { routeNames } from "@/routes";
import { getDustMonitorClassDisplayDate } from "@/views/dustMonitorClass/dustMonitorClassApi";
import type { Invitation } from "@/views/invitations/invitation";
import { getInvitations } from "@/views/invitations/invitationApi";
import { sysDate } from "@modernary/mui/utils/filters";
import { useUser } from "@modernary/mui/utils/authentication";
import { computed, defineComponent, ref, watch } from "vue";

interface InvitationItem extends LandingCardEntityBase {
  primaryLabel: string;
  secondaryLabel: string;
  additionalInfo?: string;
}

/** Dust Monitor Classes run over two days, so show the combined-day label. */
const classLabel = (invitation: Invitation) => {
  const dustMonitorClass = invitation.dustMonitorClass;
  if (!dustMonitorClass) return "Dust Monitor Class";
  const dates = getDustMonitorClassDisplayDate(dustMonitorClass);
  return dates ? `Dust Monitor Class · ${dates}` : "Dust Monitor Class";
};

const toInvitationItem = (invitation: Invitation): InvitationItem => ({
  id: invitation.id,
  to: { name: routeNames.invitation.respond, params: { token: invitation.token } },
  primaryLabel: classLabel(invitation),
  secondaryLabel: invitation.dustMonitorClass?.location ?? "Respond to take a seat in this class",
  additionalInfo: invitation.invitedAt ? `Invited ${sysDate.shortDateString(invitation.invitedAt)}` : undefined,
});

export default defineComponent({
  name: "HomeExternalInvitations",
  components: {
    LandingCard,
    LandingCardEntityList,
    LandingCardEntityListItem,
  },
  setup() {
    const { userRef } = useUser();
    const items = ref<InvitationItem[]>([]);
    const isAuthenticated = computed(() => userRef.value.isAuthenticated);

    const load = async () => {
      try {
        const response = await getInvitations({ isPending: true });
        items.value = (response?.data?._items ?? []).map(toInvitationItem);
      } catch (error) {
        items.value = [];
        console.error("Unable to load pending invitations", error);
      }
    };

    // Sign-in can settle after mount, so follow the flag rather than loading once.
    watch(
      isAuthenticated,
      (authenticated) => {
        if (authenticated) load();
        else items.value = [];
      },
      { immediate: true },
    );

    return { items };
  },
});
</script>
